import { startTransition, useEffect, useMemo, useState } from "react"

import {
  PublicLookupSectionSkeleton,
  PublicSidebarSectionSkeleton,
} from "@/components/app/public-loading-skeleton"
import { Button } from "@/components/ui/button"
import {
  createRegistrationOtpChallengeState,
  failOtpChallenge,
  formatOtpCountdown,
  getOtpChallengeTimers,
  markOtpSending,
  markOtpVerified,
  markOtpVerifying,
  resetOtpChallenge,
  startOtpChallenge,
  type RegistrationOtpChallengeState,
} from "@/features/public/register/otp-challenge"
import {
  finalizeGuardianApproval,
  getGuardianApprovalNotice,
  lookupGuardianApprovalTag,
  startGuardianApprovalOtp,
  verifyGuardianApprovalOtp,
} from "@/features/public/register/guardian-approval-api"
import {
  getApiFailure,
  isApiError,
  type ConsentNoticeVersionSummary,
  type PublicLookupData,
} from "@/lib/api/client"

const SECTION_CLASS = "shell-panel rounded-4xl border border-border/70 p-5 sm:p-6"
const INPUT_CLASS =
  "mt-2 w-full rounded-2xl border border-border bg-background/90 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
const LABEL_CLASS = "text-sm font-medium text-foreground"
const BANNER_BASE = "rounded-3xl border px-4 py-3 text-sm leading-6"
const HELP_CLASS = "mt-2 text-xs leading-5 text-muted-foreground"

type LookupStatus = "idle" | "invalid" | "loading" | "ready"

interface GuardianApprovalPageProps {
  initialTag?: string | null
}

interface NoticeSection {
  body?: string
  heading?: string
}

function getNoticeSections(notice: ConsentNoticeVersionSummary | null): NoticeSection[] {
  const sections = notice?.contentJson?.sections
  return Array.isArray(sections) ? (sections as NoticeSection[]) : []
}

function maskPhone(value: string | null | undefined) {
  if (!value) {
    return "the listed guardian phone"
  }

  const digits = value.replace(/\D+/g, "")
  const suffix = digits.slice(-4)
  return suffix ? `the listed guardian phone ending in ${suffix}` : "the listed guardian phone"
}

function SummaryStat(props: { label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/70 p-4">
      <dt className="text-xs tracking-[0.2em] text-muted-foreground uppercase">
        {props.label}
      </dt>
      <dd className="mt-2 text-base font-semibold text-foreground">
        {props.value}
      </dd>
    </div>
  )
}

export function GuardianApprovalPage({
  initialTag = null,
}: GuardianApprovalPageProps) {
  const [tag, setTag] = useState(initialTag ?? "")
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>(
    initialTag ? "loading" : "idle"
  )
  const [lookupMessage, setLookupMessage] = useState(
    initialTag
      ? "Checking the pending under-18 registration..."
      : "Enter the pending SafetyQR tag to continue."
  )
  const [lookupData, setLookupData] = useState<PublicLookupData | null>(null)
  const [notice, setNotice] = useState<ConsentNoticeVersionSummary | null>(null)
  const [noticeAccepted, setNoticeAccepted] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState("")
  const [formError, setFormError] = useState("")
  const [otpCode, setOtpCode] = useState("")
  const [otpChallenge, setOtpChallenge] =
    useState<RegistrationOtpChallengeState>(
      createRegistrationOtpChallengeState
    )
  const [tickNow, setTickNow] = useState(() => Date.now())
  const [submitStatus, setSubmitStatus] = useState<"idle" | "saving">("idle")
  const [successTitle, setSuccessTitle] = useState("")
  const [successMessage, setSuccessMessage] = useState("")
  const [bootstrapPending, setBootstrapPending] = useState(() => Boolean(initialTag))

  useEffect(() => {
    if (!otpChallenge.timerActive) {
      return
    }

    const timerId = window.setInterval(() => {
      setTickNow(Date.now())
    }, 1000)

    return () => {
      window.clearInterval(timerId)
    }
  }, [otpChallenge.timerActive])

  useEffect(() => {
    let cancelled = false

    async function loadNotice() {
      try {
        const activeNotice = await getGuardianApprovalNotice()

        if (!cancelled) {
          setNotice(activeNotice)
        }
      } catch (error) {
        if (!cancelled) {
          setFormError(
            error instanceof Error
              ? error.message
              : "The guardian approval notice could not be loaded."
          )
        }
      }
    }

    void loadNotice()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!initialTag) {
      return
    }

    const confirmedInitialTag = initialTag
    let cancelled = false

    async function confirmInitialTag() {
      try {
        const response = await lookupGuardianApprovalTag(confirmedInitialTag)

        if (cancelled) {
          return
        }

        startTransition(() => {
          setLookupData(response.data)
          setLookupStatus("ready")
          setLookupMessage("Pending guardian approval found. Review the notice before activation.")
          setBootstrapPending(false)
        })
      } catch (error) {
        const failure = getApiFailure(error)

        if (cancelled) {
          return
        }

        if (isApiError(error) && failure?.code === "INVALID_TAG") {
          setLookupStatus("invalid")
          setLookupData(null)
          setLookupMessage(`The tag "${confirmedInitialTag}" is invalid or has not been issued yet.`)
          setBootstrapPending(false)
          return
        }

        setLookupStatus("invalid")
        setLookupData(null)
        setLookupMessage(
          error instanceof Error
            ? error.message
            : "The pending guardian approval could not be loaded."
        )
        setBootstrapPending(false)
      }
    }

    void confirmInitialTag()

    return () => {
      cancelled = true
    }
  }, [initialTag])

  const timers = useMemo(
    () => getOtpChallengeTimers(otpChallenge, tickNow),
    [otpChallenge, tickNow]
  )
  const guardianPhoneHint = maskPhone(lookupData?.compliance?.guardianPhone ?? null)
  const readyForApproval = Boolean(
    lookupData?.compliance?.guardianRequired
    && lookupData.compliance?.registrationStatus === "pending_guardian_approval"
  )
  const alreadyApproved = lookupData?.compliance?.guardianApprovalStatus === "approved"
    || lookupData?.activationState === "activated"
  const isBootstrapPending = bootstrapPending && lookupStatus === "loading" && !lookupData

  function resetOtp(message: string) {
    setOtpChallenge(resetOtpChallenge(message))
    setOtpCode("")
  }

  async function confirmTag() {
    const trimmedTag = tag.trim()

    if (!trimmedTag) {
      setFormError("Enter the pending SafetyQR tag before continuing.")
      return
    }

    setLookupStatus("loading")
    setLookupMessage("Checking the pending under-18 registration...")
    setFormError("")

    try {
      const response = await lookupGuardianApprovalTag(trimmedTag)

      startTransition(() => {
        setLookupData(response.data)
        setLookupStatus("ready")
        setLookupMessage("Pending guardian approval found. Review the notice before activation.")
      })
    } catch (error) {
      const failure = getApiFailure(error)

      if (isApiError(error) && failure?.code === "INVALID_TAG") {
        setLookupStatus("invalid")
        setLookupData(null)
        setLookupMessage("This SafetyQR tag is invalid or has not been issued.")
        return
      }

      setLookupStatus("invalid")
      setLookupData(null)
      setLookupMessage(
        error instanceof Error
          ? error.message
          : "The pending guardian approval could not be loaded."
      )
    }
  }

  async function handleSendOtp() {
    if (!lookupData?.tagType || !lookupData?.tag) {
      setFormError("Check the pending tag before requesting a guardian approval code.")
      return
    }

    if (!phoneNumber.trim()) {
      setFormError("Enter the listed guardian phone number before requesting a code.")
      return
    }

    setFormError("")
    setOtpChallenge((currentChallenge) => markOtpSending(currentChallenge))

    try {
      const response = await startGuardianApprovalOtp({
        phoneNumber: phoneNumber.trim(),
        stickerId: lookupData.tag,
        tag: lookupData.tag,
        tagType: lookupData.tagType,
      })

      setOtpCode("")
      setOtpChallenge((currentChallenge) =>
        startOtpChallenge(currentChallenge, {
          ...response,
          phoneNumber: phoneNumber.trim(),
        })
      )
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to send the guardian approval code."

      setOtpChallenge((currentChallenge) =>
        failOtpChallenge(currentChallenge, message)
      )
      setFormError(message)
    }
  }

  async function handleVerifyOtp() {
    if (!otpChallenge.verificationId) {
      setFormError("Request a guardian approval code before verifying it.")
      return
    }

    if (otpCode.trim().length !== 6) {
      setFormError("Enter the six-digit guardian approval code.")
      return
    }

    setFormError("")
    setOtpChallenge((currentChallenge) => markOtpVerifying(currentChallenge))

    try {
      const response = await verifyGuardianApprovalOtp({
        otp: otpCode.trim(),
        phoneNumber: phoneNumber.trim(),
        verificationId: otpChallenge.verificationId,
      })

      if (!response.approved) {
        throw new Error("That guardian approval code was not accepted.")
      }

      setOtpChallenge((currentChallenge) => markOtpVerified(currentChallenge))
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to verify the guardian approval code."

      setOtpChallenge((currentChallenge) =>
        failOtpChallenge(currentChallenge, message)
      )
      setFormError(message)
    }
  }

  async function handleApprove() {
    if (!lookupData?.tagType || !lookupData?.tag) {
      setFormError("Check the pending tag before approving it.")
      return
    }

    if (!readyForApproval) {
      setFormError("This tag is not waiting for guardian approval.")
      return
    }

    if (!notice?.id) {
      setFormError("The active guardian approval notice is not available.")
      return
    }

    if (!noticeAccepted) {
      setFormError("Review and accept the guardian approval notice before continuing.")
      return
    }

    if (!otpChallenge.verificationId || !otpChallenge.verified) {
      setFormError("Verify the guardian phone number before approving this profile.")
      return
    }

    setFormError("")
    setSubmitStatus("saving")

    try {
      await finalizeGuardianApproval({
        noticeVersionId: notice.id,
        phoneNumber: phoneNumber.trim(),
        stickerId: lookupData.tag,
        tag: lookupData.tag,
        tagType: lookupData.tagType,
        verificationId: otpChallenge.verificationId,
      })

      startTransition(() => {
        setSuccessTitle("Guardian approval completed")
        setSuccessMessage(
          "The under-18 SafetyQR profile is now active, public reporting access is enabled, and the guardian phone is the authorized owner contact."
        )
      })
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "The guardian approval could not be completed."
      )
      setSubmitStatus("idle")
    }
  }

  if (successTitle) {
    return (
      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
        <div className={SECTION_CLASS}>
          <span className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
            Guardian approval complete
          </span>
          <h2 className="mt-3 font-heading text-3xl text-balance">
            {successTitle}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
            {successMessage}
          </p>
          <dl className="mt-6 grid gap-4 sm:grid-cols-2">
            <SummaryStat label="Tag" value={tag} />
            <SummaryStat
              label="Guardian phone"
              value={phoneNumber || lookupData?.compliance?.guardianPhone || "Verified"}
            />
            <SummaryStat label="Activation" value="Activated" />
            <SummaryStat label="Verification" value="Approved" />
          </dl>
        </div>

        <aside className={SECTION_CLASS}>
          <h3 className="font-semibold">What happens next</h3>
          <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
            <li>The profile can now open the normal reporting flow on future scans.</li>
            <li>The approved guardian phone is now the authorized owner-access contact.</li>
            <li>Withdrawal, deletion, and correction actions remain available through the owner portal.</li>
          </ul>
        </aside>
      </section>
    )
  }

  return (
    <section className="grid gap-5 xl:grid-cols-[minmax(0,1.24fr)_minmax(18rem,0.82fr)]">
      <div className="space-y-5">
        {isBootstrapPending ? (
          <PublicLookupSectionSkeleton testId="guardian-approval-lookup-skeleton" />
        ) : (
          <section className={SECTION_CLASS}>
            <span className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
              Guardian approval
            </span>
            <h2 className="mt-3 font-heading text-3xl text-balance">
              Approve an under-18 SafetyQR profile.
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
              Guardian approval is required before a minor profile can activate and open public reporting.
            </p>

            <label className="mt-6 block">
              <span className={LABEL_CLASS}>Pending SafetyQR tag</span>
              <input
                className={INPUT_CLASS}
                name="guardianApprovalTag"
                onChange={(event) => {
                  setTag(event.target.value)
                  setLookupStatus("idle")
                  setLookupData(null)
                  setLookupMessage("Enter the pending SafetyQR tag to continue.")
                  setFormError("")
                  resetOtp("Tag changed. Request a fresh guardian approval code.")
                }}
                value={tag}
              />
              <p className={HELP_CLASS}>
                Enter the under-18 tag that is still waiting for guardian approval.
              </p>
            </label>

            <div className="mt-4 flex items-center gap-3">
              <Button
                disabled={lookupStatus === "loading"}
                onClick={() => void confirmTag()}
                type="button"
                variant="outline"
              >
                Check pending tag
              </Button>
            </div>

            <div
              className={`${BANNER_BASE} mt-4 ${
                lookupStatus === "invalid"
                  ? "border-rose-300/70 bg-rose-100/70 text-rose-950"
                  : lookupStatus === "ready"
                    ? "border-emerald-300/70 bg-emerald-100/70 text-emerald-950"
                    : "border-border/70 bg-background/70 text-muted-foreground"
              }`}
            >
              {lookupMessage}
            </div>
          </section>
        )}

        <section className={SECTION_CLASS}>
          <h3 className="font-semibold">Pending profile</h3>
          {lookupData ? (
            <>
              <dl className="mt-5 grid gap-4 md:grid-cols-2">
                <SummaryStat label="Tag" value={lookupData.tag} />
                <SummaryStat
                  label="Status"
                  value={lookupData.compliance?.registrationStatus ?? lookupData.activationState ?? "Unknown"}
                />
                <SummaryStat
                  label="Guardian approval"
                  value={lookupData.compliance?.guardianApprovalStatus ?? "Unknown"}
                />
                <SummaryStat
                  label="Guardian relation"
                  value={lookupData.compliance?.guardianRelationship ?? "Pending"}
                />
              </dl>

              {alreadyApproved ? (
                <div className={`${BANNER_BASE} mt-5 border-emerald-300/70 bg-emerald-100/70 text-emerald-950`}>
                  This profile is already active. No additional guardian approval is needed.
                </div>
              ) : !readyForApproval ? (
                <div className={`${BANNER_BASE} mt-5 border-amber-300/70 bg-amber-100/70 text-amber-950`}>
                  This tag is not currently in the pending guardian-approval state.
                </div>
              ) : null}
            </>
          ) : (
            <p className="mt-4 text-sm leading-6 text-muted-foreground">
              Check the pending tag first so the guardian approval record can be loaded.
            </p>
          )}
        </section>

        <section className={SECTION_CLASS}>
          <h3 className="font-semibold">Guardian notice</h3>
          {notice ? (
            <>
              <div className={`${BANNER_BASE} mt-4 border-border/70 bg-background/70 text-foreground`}>
                <strong className="block font-medium">{notice.title}</strong>
                {notice.summary ? (
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {notice.summary}
                  </p>
                ) : null}
              </div>

              <div className="mt-5 grid gap-4">
                {getNoticeSections(notice).map((section) => (
                  <div
                    className="rounded-3xl border border-border/70 bg-background/70 p-4"
                    key={`${section.heading}-${section.body}`}
                  >
                    {section.heading ? (
                      <h4 className="text-sm font-semibold text-foreground">
                        {section.heading}
                      </h4>
                    ) : null}
                    {section.body ? (
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        {section.body}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>

              <label className="mt-5 flex items-start gap-3 rounded-3xl border border-border/70 bg-background/70 p-4 text-sm leading-6 text-muted-foreground">
                <input
                  checked={noticeAccepted}
                  onChange={(event) => setNoticeAccepted(event.target.checked)}
                  type="checkbox"
                />
                <span>
                  I confirm that I am the listed parent or legal guardian and I approve activation of this under-18 SafetyQR profile.
                </span>
              </label>
            </>
          ) : (
            <p className="mt-4 text-sm leading-6 text-muted-foreground">
              Loading the current guardian approval notice.
            </p>
          )}
        </section>

        <section className={SECTION_CLASS}>
          <h3 className="font-semibold">Guardian OTP verification</h3>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Use {guardianPhoneHint} that was captured during registration. Subject or self-registration phone numbers cannot complete activation.
          </p>

          <label className="mt-5 block">
            <span className={LABEL_CLASS}>Guardian phone</span>
            <input
              className={INPUT_CLASS}
              name="guardianPhone"
              onChange={(event) => {
                setPhoneNumber(event.target.value)
                resetOtp("Phone number changed. Request a fresh guardian approval code.")
              }}
              type="tel"
              value={phoneNumber}
            />
          </label>

          <div
            className={`${BANNER_BASE} mt-4 ${
              otpChallenge.tone === "success"
                ? "border-emerald-300/70 bg-emerald-100/70 text-emerald-950"
                : otpChallenge.tone === "error"
                  ? "border-rose-300/70 bg-rose-100/70 text-rose-950"
                  : otpChallenge.tone === "pending"
                    ? "border-amber-300/70 bg-amber-100/70 text-amber-950"
                    : "border-border/70 bg-background/70 text-muted-foreground"
            }`}
          >
            <strong className="block font-medium">{otpChallenge.title}</strong>
            <p className="mt-1">{otpChallenge.description}</p>
            {otpChallenge.verificationId && !otpChallenge.verified ? (
              <p className="mt-2 text-xs">
                Code expires in {formatOtpCountdown(timers.expiresRemainingMs)}.
              </p>
            ) : null}
            {otpChallenge.debugOtp ? (
              <p className="mt-2 text-xs">
                Development code: {otpChallenge.debugOtp}
              </p>
            ) : null}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-[minmax(0,1fr)_auto_auto]">
            <label className="block">
              <span className={LABEL_CLASS}>Verification code</span>
              <input
                className={INPUT_CLASS}
                inputMode="numeric"
                name="guardianOtpCode"
                onChange={(event) =>
                  setOtpCode(event.target.value.replace(/\D+/g, "").slice(0, 6))
                }
                value={otpCode}
              />
            </label>

            <div className="self-end">
              <Button
                disabled={
                  !readyForApproval
                  || otpChallenge.sendLoading
                  || otpChallenge.verified
                  || timers.resendRemainingMs > 0
                  || alreadyApproved
                }
                onClick={() => void handleSendOtp()}
                type="button"
                variant="outline"
              >
                {otpChallenge.sendLoading
                  ? "Sending..."
                  : timers.resendRemainingMs > 0
                    ? `Resend in ${formatOtpCountdown(timers.resendRemainingMs)}`
                    : otpChallenge.verificationId
                      ? "Resend code"
                      : "Send code"}
              </Button>
            </div>

            <div className="self-end">
              <Button
                disabled={
                  otpChallenge.verifyLoading
                  || otpChallenge.verified
                  || otpCode.trim().length !== 6
                  || alreadyApproved
                }
                onClick={() => void handleVerifyOtp()}
                type="button"
              >
                {otpChallenge.verifyLoading ? "Verifying..." : "Verify code"}
              </Button>
            </div>
          </div>
        </section>

        {formError ? (
          <div className={`${BANNER_BASE} border-rose-300/70 bg-rose-100/70 text-rose-950`}>
            {formError}
          </div>
        ) : null}

        <Button
          disabled={!readyForApproval || alreadyApproved || submitStatus === "saving"}
          onClick={() => void handleApprove()}
          type="button"
        >
          {submitStatus === "saving" ? "Approving..." : "Approve and activate profile"}
        </Button>
      </div>

      <aside className="space-y-5">
        {isBootstrapPending ? (
          <PublicSidebarSectionSkeleton
            rows={3}
            testId="guardian-approval-sidebar-skeleton"
          />
        ) : (
          <section className={SECTION_CLASS}>
            <h3 className="font-semibold">Activation state</h3>
            <div className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
              <p>Tag status: {lookupStatus}</p>
              <p>Registration: {lookupData?.compliance?.registrationStatus ?? "unresolved"}</p>
              <p>Guardian approval: {lookupData?.compliance?.guardianApprovalStatus ?? "unresolved"}</p>
            </div>
          </section>
        )}

        <section className={SECTION_CLASS}>
          <h3 className="font-semibold">Approval rules</h3>
          <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
            <li>Only the listed guardian phone can complete approval.</li>
            <li>Subject or self-registration phones are rejected for activation finalization.</li>
            <li>Approval activates the profile and sets the guardian phone as the owner-access contact.</li>
          </ul>
        </section>
      </aside>
    </section>
  )
}
